import { useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, X, Copy, Download, Sparkles, Check, BadgeCheck } from "lucide-react";

/**
 * Instagram Copy-Paste Generator modal.
 *
 * The Instagram Graph API requires Meta App Review + a business account link,
 * which the seller has not yet completed. As a bridge, this modal generates an
 * editorial caption + hashtags (via Emergent LLM) and packages the item's
 * photos into a ZIP the seller can AirDrop to their phone and paste manually.
 *
 * Once posted, the seller clicks "Mark as posted" and we set
 * `posted_to_instagram=true` so the piece drops out of the still-to-post queue.
 */
export default function InstagramPrepModal({ item, onClose, onPosted }) {
  const [tone, setTone] = useState("boutique");
  const [maxTags, setMaxTags] = useState(15);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { caption, hashtags, full_caption }
  const [copiedField, setCopiedField] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [marking, setMarking] = useState(false);

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post(`/inventory/${item.id}/instagram/generate`, {
        tone,
        max_hashtags: maxTags,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not generate caption.");
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
      toast.success("copied to clipboard.");
    } catch {
      toast.error("could not copy — select and copy manually.");
    }
  };

  const downloadZip = async () => {
    setZipping(true);
    try {
      const res = await api.get(`/inventory/${item.id}/instagram/photos-zip`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `maison-pilet-${(item.sku || item.id).slice(0, 20)}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("photos zip downloaded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not download photos.");
    } finally {
      setZipping(false);
    }
  };

  const markPosted = async () => {
    if (!window.confirm("Mark this piece as posted to Instagram? It will drop out of the still-to-post queue.")) return;
    setMarking(true);
    try {
      await api.post(`/inventory/${item.id}/instagram/mark-posted`);
      toast.success("marked as posted to instagram.");
      onPosted && onPosted();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not mark as posted.");
    } finally {
      setMarking(false);
    }
  };

  const tones = [
    { key: "boutique", label: "boutique · editorial", hint: "warm, understated luxury voice (default)" },
    { key: "luxury", label: "luxury · refined", hint: "elevated, whitespace, deliberate" },
    { key: "casual", label: "casual · playful", hint: "friendly, conversational" },
    { key: "sustainable", label: "sustainable", hint: "leads with second-life story" },
    { key: "promotional", label: "promotional", hint: "price-forward, call-to-action" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-charcoal/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
      data-testid="ig-prep-modal-overlay"
    >
      <div
        className="bg-ivory border border-taupe/50 max-w-2xl w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="ig-prep-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-taupe/40 px-6 py-4">
          <div>
            <h2 className="font-serif text-2xl lowercase text-charcoal flex items-center gap-2">
              <Instagram className="w-5 h-5 text-brass" /> prep for instagram
            </h2>
            <p className="text-xs text-espresso mt-1 lowercase">
              generate a caption + hashtags, download photos, paste on your phone.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="ig-prep-close-btn"
            className="text-taupe hover:text-charcoal p-1 -m-1"
            aria-label="close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {/* Item summary */}
          <div className="flex items-center gap-4 pb-4 border-b border-taupe/30">
            {item.photos?.[0] && (
              <div className="w-16 h-16 border border-taupe/30 overflow-hidden flex-shrink-0 bg-linen">
                <img src={fileUrl(item.photos[0])} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-serif text-lg lowercase text-charcoal truncate">{item.title || "(untitled)"}</p>
              <p className="text-[0.7rem] uppercase tracking-widest text-taupe mt-0.5">
                {item.brand || "—"} · {item.size || "—"} · {(item.photos || []).length} photo{(item.photos || []).length === 1 ? "" : "s"}
              </p>
            </div>
            {item.posted_to_instagram && (
              <span className="text-[0.6rem] uppercase tracking-widest text-emerald-700 flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> already posted
              </span>
            )}
          </div>

          {/* Tone picker */}
          <div>
            <p className="editorial-label mb-2">tone</p>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="ig-tone-picker">
              {tones.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTone(t.key)}
                  data-testid={`ig-tone-${t.key}`}
                  className={`text-left px-3 py-2 border transition-colors ${
                    tone === t.key
                      ? "border-brass bg-brass/10 text-charcoal"
                      : "border-taupe/40 hover:border-taupe text-espresso"
                  }`}
                >
                  <p className="text-xs lowercase tracking-widest font-medium">{t.label}</p>
                  <p className="text-[0.65rem] text-taupe mt-0.5">{t.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Hashtag count */}
          <div className="flex items-center gap-3">
            <label className="editorial-label" htmlFor="ig-max-hashtags">hashtags</label>
            <input
              id="ig-max-hashtags"
              type="number"
              min={5}
              max={30}
              value={maxTags}
              onChange={(e) => setMaxTags(Math.max(5, Math.min(30, Number(e.target.value) || 15)))}
              className="border border-taupe/40 px-3 py-1.5 text-sm w-20 bg-white"
              data-testid="ig-max-hashtags"
            />
            <span className="text-xs text-espresso">(5–30)</span>
          </div>

          {/* Generate button */}
          <button
            onClick={generate}
            disabled={generating}
            data-testid="ig-generate-btn"
            className="w-full bg-charcoal hover:bg-brass text-ivory px-5 py-3 text-xs tracking-[0.25em] uppercase flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            {generating ? "writing…" : result ? "regenerate" : "generate caption"}
          </button>

          {/* Result */}
          {result && (
            <div className="space-y-4" data-testid="ig-result-panel">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="editorial-label">caption</p>
                  <button
                    onClick={() => copy(result.caption, "caption")}
                    className="text-xs text-brass hover:text-charcoal flex items-center gap-1"
                    data-testid="ig-copy-caption-btn"
                  >
                    {copiedField === "caption" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedField === "caption" ? "copied" : "copy"}
                  </button>
                </div>
                <textarea
                  readOnly
                  value={result.caption}
                  rows={5}
                  data-testid="ig-caption-output"
                  className="w-full border border-taupe/40 bg-white p-3 text-sm text-charcoal font-serif leading-relaxed resize-y"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="editorial-label">hashtags ({result.hashtags.length})</p>
                  <button
                    onClick={() => copy(result.hashtags.map((h) => `#${h}`).join(" "), "hashtags")}
                    className="text-xs text-brass hover:text-charcoal flex items-center gap-1"
                    data-testid="ig-copy-hashtags-btn"
                  >
                    {copiedField === "hashtags" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedField === "hashtags" ? "copied" : "copy"}
                  </button>
                </div>
                <div className="border border-taupe/40 bg-white p-3 text-xs text-espresso flex flex-wrap gap-1.5" data-testid="ig-hashtags-output">
                  {result.hashtags.map((h) => (
                    <span key={h} className="bg-linen border border-taupe/30 px-2 py-0.5 rounded-sm">#{h}</span>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="editorial-label">full paste-ready</p>
                  <button
                    onClick={() => copy(result.full_caption, "full")}
                    className="text-xs text-brass hover:text-charcoal flex items-center gap-1"
                    data-testid="ig-copy-full-btn"
                  >
                    {copiedField === "full" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedField === "full" ? "copied" : "copy all"}
                  </button>
                </div>
                <p className="text-[0.65rem] text-taupe italic">caption + blank line + hashtags — ready to paste into IG.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-taupe/40 px-6 py-4 flex flex-wrap gap-3 justify-between items-center bg-linen/40">
          <button
            onClick={downloadZip}
            disabled={zipping || !(item.photos || []).length}
            data-testid="ig-download-zip-btn"
            className="border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory px-4 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="w-3 h-3" />
            {zipping ? "packing…" : "download photos (zip)"}
          </button>
          <button
            onClick={markPosted}
            disabled={marking || item.posted_to_instagram}
            data-testid="ig-mark-posted-btn"
            className="bg-brass hover:bg-espresso text-ivory px-4 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
          >
            <BadgeCheck className="w-3 h-3" />
            {item.posted_to_instagram ? "already marked" : marking ? "marking…" : "mark as posted"}
          </button>
        </div>
      </div>
    </div>
  );
}
